import { FC, useRef, useState, useEffect } from 'react';
import { KatButton } from '@amzn/katal-react';
import { TimeInput } from 'src/components/general/TimeInput';
import { timeToSeconds, secondsToTime } from 'src/utils/DateTimeUtils';
import { formatDuration } from 'src/utils/assetInput/assetInputUtils';
import { validateVideoDurationInput } from 'src/utils/validations/Videos';
import { VideoInputState, AssetState } from 'src/types';
import { AssetConstants, StateConstants } from 'src/constants';
import { AssetStatus } from 'src/types';
import styles from './VideoDurationInput.module.css';

interface VideoDurationInputProps {
  videoState: VideoInputState;
  updateAssetState: (state: Partial<AssetState>) => void;
  videoDuration?: number;
  disabled?: boolean;
  minDuration?: number;
  maxDuration?: number;
  'data-testid': string;
}

export const VideoDurationInput: FC<VideoDurationInputProps> = ({
  videoState,
  updateAssetState,
  videoDuration,
  disabled = false,
  minDuration = AssetConstants.VIDEO_DURATION.MIN_DURATION,
  maxDuration = AssetConstants.VIDEO_DURATION.MAX_DURATION,
  'data-testid': dataTestId,
}) => {
  // Use saved values or defaults when video is uploaded
  const getInitialState = () => ({
    startTime: videoDuration ? videoState.startTime || '00:00' : StateConstants.EMPTY_STRING,
    endTime: videoDuration
      ? videoState.endTime || secondsToTime(videoDuration)
      : StateConstants.EMPTY_STRING,
  });

  const [currentState, setCurrentState] = useState(getInitialState());
  const savedStateRef = useRef<{ startTime: string; endTime: string }>({
    startTime: StateConstants.EMPTY_STRING,
    endTime: StateConstants.EMPTY_STRING,
  });

  // Sets saved state on mount and when video duration changes
  useEffect(() => {
    setCurrentState(getInitialState());
    savedStateRef.current = {
      startTime: videoState.startTime || StateConstants.EMPTY_STRING,
      endTime: videoState.endTime || StateConstants.EMPTY_STRING,
    };
  }, [videoDuration]);

  const hasChanges =
    currentState.startTime !== savedStateRef.current.startTime ||
    currentState.endTime !== savedStateRef.current.endTime;

  // Validation
  const { isValid, endError, durationError } = validateVideoDurationInput(
    currentState.startTime,
    currentState.endTime,
    videoDuration,
    minDuration,
    maxDuration,
  );

  const displayDuration =
    currentState.startTime && currentState.endTime
      ? formatDuration(
          Math.max(0, timeToSeconds(currentState.endTime) - timeToSeconds(currentState.startTime)),
        )
      : '';

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    savedStateRef.current = currentState;

    updateAssetState({
      startTime: currentState.startTime,
      endTime: currentState.endTime,
      status: AssetStatus.PENDING_TRANSFORMATION,
    });
  };

  const handleCancel = () => {
    setCurrentState(savedStateRef.current);
  };

  return (
    <form className={styles.container} data-testid={dataTestId} onSubmit={handleSave}>
      <div className={styles.timeInputs}>
        <TimeInput
          label="Start Time"
          value={currentState.startTime}
          onChange={(value) => setCurrentState({ ...currentState, startTime: value })}
          disabled={disabled}
          placeholder={!videoDuration ? 'MM:SS' : undefined}
          data-testid={`${dataTestId}-start-time`}
        />
        <TimeInput
          label="End Time"
          value={currentState.endTime}
          onChange={(value) => setCurrentState({ ...currentState, endTime: value })}
          error={endError}
          disabled={disabled}
          placeholder={!videoDuration ? 'MM:SS' : undefined}
          data-testid={`${dataTestId}-end-time`}
        />
      </div>

      {(durationError || displayDuration) && (
        <div
          className={durationError ? styles.durationError : styles.durationInfo}
          data-testid={`${dataTestId}-duration-${durationError ? 'error' : 'info'}`}
        >
          {durationError || `Duration: ${displayDuration}`}
        </div>
      )}

      <div className={styles.buttonContainer}>
        <KatButton
          variant="tertiary"
          onClick={handleCancel}
          disabled={disabled || !hasChanges}
          data-testid={`${dataTestId}-cancel-button`}
        >
          Cancel
        </KatButton>
        <KatButton
          variant="primary"
          disabled={!currentState.startTime || !currentState.endTime || !isValid || disabled}
          data-testid={`${dataTestId}-save-button`}
          type="submit"
        >
          Save
        </KatButton>
      </div>
    </form>
  );
};
